import { getSupabaseAccessToken, hasFirebaseUser } from "./supabaseSession";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

/**
 * Shared fetch helper for Supabase PostgREST calls.
 * Used by supabaseSearch.js, supabaseProjects.js, and supabaseUsers.js.
 *
 * When a rep is signed in (Firebase), requests carry a real Supabase session
 * minted by the users-mintSupabaseSession callable, so Postgres sees
 * `authenticated`, not `anon`. Pre-auth callers (shareBridge.js,
 * v1Lockout.js) get no token and proceed as anon, unchanged.
 *
 * The `apikey` header stays the anon key in both cases — PostgREST requires
 * it as the gateway key; authorization is carried by the Bearer token.
 */
export async function supabaseFetch(path, options = {}) {
  const url = `${SUPABASE_URL}/rest/v1${path}`;
  const token = await getSupabaseAccessToken({ signal: options.signal });

  const doFetch = (bearer) =>
    fetch(url, {
      ...options,
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${bearer}`,
        "Content-Type": "application/json",
        ...options.headers,
      },
    });

  let response = await doFetch(token ?? SUPABASE_ANON_KEY);

  // 401 = expired/invalid JWT; 403 = revoked EXECUTE/SELECT (PostgREST uses
  // 403 for permission errors, so an anon call hitting an authenticated-only
  // function lands here). Either way: if a rep is signed in, mint a fresh
  // session once and retry.
  if (
    (response.status === 401 || response.status === 403) &&
    hasFirebaseUser()
  ) {
    const freshToken = await getSupabaseAccessToken({
      signal: options.signal,
      forceRefresh: true,
    });
    if (freshToken && freshToken !== token) {
      response = await doFetch(freshToken);
    }
  }

  if (!response.ok) {
    throw new Error(`Supabase request failed: ${response.status} ${response.statusText}`);
  }

  return response.json();
}
